/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  getWorkflowStepResult,
  PageAlignmentDetails,
  WorkflowPageAlignmentStepResult
} from '~/lib/mbue-api/services/workflowStepResult.ts'
import { type WebViewerInstance } from '@pdftron/webviewer'
import { useUnsavedChangesWarning } from '~/hooks/useUnsavedChangesWarning'
import { isNil } from 'lodash'

import Textarea from '~/components/ui/Textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from 'components/ui/Table'
import Button from 'components/ui/Button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '~/components/ui/DropdownMenu'
import {
  SquarePenIcon,
  FlipHorizontalIcon,
  MessageCirclePlusIcon,
  XIcon,
  Loader2
} from 'lucide-react'
import { Label } from '@radix-ui/react-label'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '~/components/ui/AlertDialog'

declare global {
  interface Window {
    WebViewer?: {
      getInstance: (viewer?: HTMLDivElement) => WebViewerInstance
      length: number
    }
  }
}

const statusNames = {
  confirming: 'confirm',
  confirmed: 'confirmed',
  review: 'review'
}

const statusColors = {
  confirmed: 'bg-[#34A853] text-white hover:bg-[#34A853]/90',
  confirming: 'bg-[#E44386] text-white hover:bg-[#E44386]/90',
  review: 'bg-gray-200'
}

const furtherStatusNames = {
  review: 'confirming',
  confirming: 'confirmed',
  confirmed: 'review'
}

const ConfirmMatchedSpecifiedProductsTable: FC<{
  highlightPdfText: (p: PageAlignmentDetails) => void
  projectId: number
  workflowInstanceId: string
  updateStepResult: (
    p: { result: WorkflowPageAlignmentStepResult },
    q: boolean
  ) => Promise<void>
  viewerRef: React.RefObject<HTMLDivElement>
  isStepCompleted: boolean
}> = ({
  highlightPdfText,
  workflowInstanceId,
  projectId,
  updateStepResult,
  viewerRef,
  isStepCompleted
}) => {
  const navigate = useNavigate()
  const [pageAlignmentData, setPageAlignmentData] =
    useState<WorkflowPageAlignmentStepResult>()

  const [products, setProducts] = useState<PageAlignmentDetails[]>([])

  const [reviewStates, setReviewStates] = useState<
    { productIndex: number; status: string }[]
  >([])

  const [editProductRequirement, setEditProductRequirement] = useState<{
    productIndex: number
    action: 'fix' | 'comment'
  } | null>(null)

  const [unconfirmProductRequirement, setUnconfirmProductRequirement] = useState<
    number | null
  >(null)

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const [isSaving, setIsSaving] = useState(false)

  const blocker = useUnsavedChangesWarning(hasUnsavedChanges)

  useEffect(() => {
    const fetchProducts = async () => {
      if (!workflowInstanceId || !projectId) {
        return
      }

      const dataResponse = (await getWorkflowStepResult({
        projectId,
        workflowInstanceId: workflowInstanceId,
        stepIndex: 4
      })) as unknown as WorkflowPageAlignmentStepResult[]

      const data = (Array.isArray(dataResponse)
        ? dataResponse[0]
        : dataResponse) as unknown as WorkflowPageAlignmentStepResult

      if (!data?.alignment_details) {
        console.error(`Failed to fetch page alignment data for project id: ${projectId}`)
        return
      }

      const productDetails = data.alignment_details

      setPageAlignmentData(data)
      setProducts(productDetails)
      setReviewStates(
        productDetails.map((product, i) => ({
          productIndex: i,
          status: product.approved ? statusNames.confirmed : statusNames.review
        }))
      )
    }

    void fetchProducts()
  }, [projectId, workflowInstanceId])

  const onUserReviewClick = (productIndex: number, status: string) => {
    if (isSaving) return

    if (products[productIndex].approved) {
      highlightPdfText?.(products[productIndex])
      setUnconfirmProductRequirement(productIndex)
      return
    }

    if (!status) {
      return
    }

    const isApproved = status == statusNames.confirmed

    setProducts((prevProducts) => {
      const updatedProducts = [...prevProducts]
      updatedProducts[productIndex].approved = isApproved
      if (!hasUnsavedChanges) {
        setHasUnsavedChanges(true)
      }
      return updatedProducts
    })

    if (status == 'confirming') {
      highlightPdfText?.(products[productIndex])
    }

    setReviewStates((prevStates) => {
      const updatedStates = prevStates.map((prevState) => {
        if (prevState.status == 'confirming') {
          prevState.status = statusNames.review
        }

        return prevState
      })

      updatedStates[productIndex].status = status

      return updatedStates
    })
  }

  const onUnconfirmProductClick = (productIndex: number) => {
    setReviewStates((prevStates) => {
      const updatedStates = [...prevStates]
      updatedStates[productIndex].status = statusNames.review

      setProducts((prevProducts) => {
        const updatedProducts = [...prevProducts]
        updatedProducts[productIndex].approved = false
        if (!hasUnsavedChanges) {
          setHasUnsavedChanges(true)
        }
        return updatedProducts
      })

      return updatedStates
    })

    setUnconfirmProductRequirement(null)
  }

  const onPageAlignmentFix = (productIndex: number) => {
    if (!window.WebViewer || !viewerRef.current) return

    const instance = window.WebViewer.getInstance(viewerRef.current)
    const { Core } = instance
    const { documentViewer } = Core
    const currentPage = documentViewer.getCurrentPage()

    const nextProducts = structuredClone(products)
    if (
      nextProducts[productIndex] &&
      Array.isArray(nextProducts[productIndex].aligned_page)
    ) {
      nextProducts[productIndex].aligned_page[0] = currentPage
    }

    setProducts(nextProducts)

    if (pageAlignmentData?.alignment_details) {
      setPageAlignmentData(() => {
        const nextPageAlignment = structuredClone(pageAlignmentData)
        nextPageAlignment.alignment_details = nextProducts
        if (!hasUnsavedChanges) {
          setHasUnsavedChanges(true)
        }
        return nextPageAlignment
      })
    }

    setEditProductRequirement(null)
  }

  const onEditProductClick = (
    statePayload: {
      productIndex: number
      action: 'fix' | 'comment'
    },
    status: string
  ) => {
    if (products[statePayload.productIndex]?.approved) {
      toast.info('This product has already been approved')
      return
    }

    setEditProductRequirement(statePayload)
    onUserReviewClick(statePayload.productIndex, status)
  }

  const onCommentSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!editProductRequirement) return

    const formData = new FormData(e.target as HTMLFormElement)
    const comment = formData.get('comment') as string

    const nextProducts = structuredClone(products)
    nextProducts[editProductRequirement.productIndex].comment = comment
    nextProducts[editProductRequirement.productIndex].is_edited = true

    setProducts(nextProducts)

    if (pageAlignmentData?.alignment_details) {
      setPageAlignmentData(() => {
        const nextPageAlignment = structuredClone(pageAlignmentData)
        nextPageAlignment.alignment_details = nextProducts
        if (!hasUnsavedChanges) {
          setHasUnsavedChanges(true)
        }
        return nextPageAlignment
      })
    }

    setEditProductRequirement(null)
  }

  const onSaveAndExit = async () => {
    if (isSaving) return

    const allApproved = products.every((product) => product.approved)
    const matchingData = structuredClone(pageAlignmentData)

    if (matchingData?.alignment_details) {
      matchingData.alignment_details = products

      try {
        setHasUnsavedChanges(false)
        setIsSaving(true)
        await updateStepResult?.({ result: matchingData }, allApproved)
        navigate(location.pathname.split('/').slice(0, -1).join('/'))
      } catch (error) {
        console.error(error)
        toast.error('Failed to save changes')
      } finally {
        setIsSaving(false)
      }
    }
  }

  const allApproved = products.every((product) => product.approved)

  return (
    <>
      <div className="w-full bg-[#333]">
        <div className="h-[52px] w-full bg-[#333] flex justify-between items-center px-2 text-sm">
          <div className="text-white flex text-xs">
            Confirm the product data matches the specified. Edit to correct any errors
            <br />
            or missing details.
          </div>
          <div className="flex gap-1 items-center justify-center">
            <Button variant="secondary" className="h-8" onClick={() => navigate(-1)}>
              Exit
            </Button>
            <Button
              className="h-8"
              onClick={() => void onSaveAndExit()}
              disabled={isStepCompleted}
            >
              {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
              {allApproved ? 'Complete & Exit' : 'Save & Exit'}
            </Button>
          </div>
        </div>
        <div className="p-2 max-h-[calc(100vh-108px)] overflow-y-auto">
          <div className="relative w-full bg-white">
            <Table
              containerClassName={
                editProductRequirement !== null || unconfirmProductRequirement !== null
                  ? 'h-[calc(100vh_-_118px)]'
                  : ''
              }
            >
              <TableHeader>
                <TableRow>
                  <TableHead className="border-r w-[10%] bg-gray-200 font-semibold">
                    Type
                  </TableHead>
                  <TableHead className="border-r w-[70%] bg-gray-200 font-semibold">
                    Products
                  </TableHead>
                  <TableHead className="border-r w-[7%] bg-gray-200 font-semibold">
                    Volts
                  </TableHead>
                  <TableHead className="border-r w-[13%] bg-gray-200 font-semibold">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product, index) => {
                  const status = reviewStates[index].status
                  let statusText = statusNames[status as keyof typeof statusNames]

                  statusText =
                    statusText.substring(0, 1).toUpperCase() + statusText.substring(1)

                  return (
                    <TableRow key={`${product.product_name}-${index}`}>
                      <TableCell className="w-[10%] border-r">{product.type}</TableCell>
                      <TableCell className="w-[70%]">{product.product_name}</TableCell>
                      <TableCell className="w-[7%] border-r">{product.voltage}</TableCell>
                      <TableCell className="w-[13%]">
                        <div className="flex gap-1 items-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                disabled={products[index]?.approved}
                              >
                                <SquarePenIcon className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="shadow-xl">
                              <DropdownMenuItem
                                className="cursor-pointer"
                                onClick={() =>
                                  onEditProductClick(
                                    {
                                      productIndex: index,
                                      action: 'fix'
                                    },
                                    'confirming'
                                  )
                                }
                              >
                                <div className="flex items-center gap-2">
                                  <FlipHorizontalIcon className="w-4 h-4" />
                                  Fix Product Page
                                </div>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="cursor-pointer"
                                onClick={() =>
                                  onEditProductClick(
                                    {
                                      productIndex: index,
                                      action: 'comment'
                                    },
                                    'confirming'
                                  )
                                }
                              >
                                <div className="flex items-center gap-2">
                                  <MessageCirclePlusIcon className="w-4 h-4" />
                                  Add Comments
                                </div>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            variant="secondary"
                            size="sm"
                            className={`h-8 min-w-24 ${statusColors[status as keyof typeof statusColors]}`}
                            onClick={() =>
                              onUserReviewClick(
                                index,
                                furtherStatusNames[
                                  status as keyof typeof furtherStatusNames
                                ]
                              )
                            }
                          >
                            {statusText}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>

            {!isNil(editProductRequirement) && (
              <>
                {/* Dialog */}
                <div
                  role="dialog"
                  className="absolute w-full h-[calc(100vh_-_118px)] top-0 left-0"
                >
                  <div className="absolute inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
                  {/* Dialog Content */}
                  <div className="absolute left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg">
                    {/* Dialog Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        {editProductRequirement.action === 'comment' && (
                          <>
                            <h2 className="text-lg font-semibold">Add Comment</h2>
                            <p className="text-sm text-gray-500">
                              Add a comment to the specified product. <br />
                              {
                                pageAlignmentData?.alignment_details?.[
                                  editProductRequirement.productIndex
                                ].product_name
                              }
                            </p>
                          </>
                        )}
                        {editProductRequirement.action === 'fix' && (
                          <>
                            <h2 className="text-lg font-semibold">
                              Fix Product Page Alignment
                            </h2>
                            <p className="text-sm text-gray-500">
                              {
                                pageAlignmentData?.alignment_details?.[
                                  editProductRequirement.productIndex
                                ].product_name
                              }
                            </p>
                          </>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="fixed top-0 right-0"
                        onClick={() => setEditProductRequirement(null)}
                      >
                        <XIcon className="w-4 h-4" />
                      </Button>
                    </div>
                    {/* Content */}
                    {editProductRequirement.action === 'comment' && (
                      <form onSubmit={onCommentSubmit}>
                        <div className="flex flex-col gap-4">
                          <div className="flex flex-col gap-1">
                            <Label className="text-sm font-medium">Comment</Label>
                            <Textarea
                              name="comment"
                              // @ts-expect-error
                              style={{ fieldSizing: 'content' }}
                              defaultValue={
                                pageAlignmentData?.alignment_details?.[
                                  editProductRequirement.productIndex
                                ].comment
                              }
                            ></Textarea>
                          </div>
                        </div>
                        {/* Footer */}
                        <div className="flex justify-end gap-2 mt-4">
                          <Button
                            variant="outline"
                            className="min-w-[108px]"
                            onClick={() => setEditProductRequirement(null)}
                          >
                            Cancel
                          </Button>
                          <Button type="submit" className="min-w-[108px]">
                            Save
                          </Button>
                        </div>
                      </form>
                    )}
                    {editProductRequirement.action === 'fix' && (
                      <div>
                        <p className="text-sm">
                          To associate this specified product with the correct page,
                          please scroll through the document on the left side and navigate
                          to the page you want to link. Once you have selected the
                          appropriate page, click on save.
                        </p>
                        <div className="flex justify-end gap-2 mt-4">
                          <Button
                            variant="outline"
                            className="min-w-[108px]"
                            onClick={() => setEditProductRequirement(null)}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            className="min-w-[108px]"
                            onClick={() =>
                              onPageAlignmentFix(editProductRequirement.productIndex)
                            }
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {!isNil(unconfirmProductRequirement) && (
              <>
                {/* Dialog */}
                <div
                  role="dialog"
                  className="absolute w-full h-[calc(100vh_-_118px)] top-0 left-0"
                >
                  <div className="absolute inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
                  {/* Dialog Content */}
                  <div className="absolute left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg">
                    {/* Dialog Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <h2 className="text-lg font-semibold">Unconfirm Product</h2>
                        <p className="text-sm text-gray-500">
                          Are you sure you want to unconfirm the product <br />
                          {
                            pageAlignmentData?.alignment_details?.[
                              unconfirmProductRequirement
                            ].product_name
                          }{' '}
                          ?
                        </p>
                      </div>
                      <Button variant="ghost" size="icon" className="fixed top-0 right-0">
                        <XIcon className="w-4 h-4" />
                      </Button>
                    </div>
                    {/* Content */}
                    <div>
                      {/* Footer */}
                      <div className="flex justify-end gap-2 mt-4">
                        <Button
                          variant="outline"
                          className="min-w-[108px]"
                          onClick={() => setUnconfirmProductRequirement(null)}
                        >
                          Cancel
                        </Button>
                        <Button
                          className="min-w-[108px]"
                          onClick={() =>
                            onUnconfirmProductClick(unconfirmProductRequirement)
                          }
                        >
                          Unconfirm
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={blocker.state === 'blocked'}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => blocker.state === 'blocked' && blocker.reset()}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => blocker.state === 'blocked' && blocker.proceed()}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default ConfirmMatchedSpecifiedProductsTable
